import { DateTime } from "luxon";
import { QuoteUnit } from "@/types/quotes";
import { Asset } from "@/types/assets";

/** Function to format time until expiration in English */
export const getFormattedTimeUntil = (start: DateTime, end: DateTime) => {
  const diff = end
    .diff(start, ["days", "hours", "minutes", "seconds"])
    .toObject();
  if (diff.days && Math.abs(diff.days) >= 2)
    return `${Math.floor(diff.days)} days`;
  if (diff.days && Math.abs(diff.days) >= 1)
    return `${Math.floor(diff.days)} day ${diff.hours && diff.hours > 0 ? `${Math.floor(diff.hours)} hours` : ""}`;
  if (diff.hours && Math.abs(diff.hours) >= 2)
    return `${Math.floor(diff.hours)} hours`;
  if (diff.hours && Math.abs(diff.hours) >= 1)
    return `${Math.floor(diff.hours)} hour ${diff.minutes && diff.minutes > 0 ? `${Math.floor(diff.minutes)} minutes` : ""}`;
  if (diff.minutes && Math.abs(diff.minutes) >= 2)
    return `${Math.floor(diff.minutes)} minutes`;
  if (diff.minutes && Math.abs(diff.minutes) >= 1)
    return `${Math.floor(diff.minutes)} minute ${diff.seconds && diff.seconds > 0 ? `${Math.floor(diff.seconds)} seconds` : ""}`;
  return `${Math.floor(diff.seconds ?? 0)} seconds`;
};

/** Function to format time until expiration in Spanish*/
export const getFormattedTimeUntilSp = (start: DateTime, end: DateTime) => {
  const diff = end
    .diff(start, ["days", "hours", "minutes", "seconds"])
    .toObject();
  if (diff.days && Math.abs(diff.days) >= 2)
    return `${Math.floor(diff.days)} días`;
  if (diff.days && Math.abs(diff.days) >= 1)
    return `${Math.floor(diff.days)} día${diff.hours && diff.hours > 0 ? `${Math.floor(diff.hours)} houras` : ""}`;
  if (diff.hours && Math.abs(diff.hours) >= 2)
    return `${Math.floor(diff.hours)} horas`;
  if (diff.hours && Math.abs(diff.hours) >= 1)
    return `${Math.floor(diff.hours)} hora ${diff.minutes && diff.minutes > 0 ? `${Math.floor(diff.minutes)} minutos` : ""}`;
  if (diff.minutes && Math.abs(diff.minutes) >= 2)
    return `${Math.floor(diff.minutes)} minutos`;
  if (diff.minutes && Math.abs(diff.minutes) >= 1)
    return `${Math.floor(diff.minutes)} minuto ${diff.seconds && diff.seconds > 0 ? `${Math.floor(diff.seconds)} segundos` : ""}`;
  return `${Math.floor(diff.seconds ?? 0)} segundos`;
};

export const formatNumber = (value: number | string, decimals: number) => {
  const [integerPart, decimalPart] = Number(value).toFixed(decimals).split(".");

  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

export const formatPrice = (price: string, decimals: number = 2) => {
  return formatNumber(price, decimals);
};

/**
 * Function to format a number to a certain number of significant digits
 * counting the integer part and the decimal part together.
 * eg 1. if the integer part has 3 and the decimal part has 6 digits and
 * we want 5 significant digits, we return: eg. 123.456
 * eg 2. if the integer part has 3 and the decimal part has 6 digits and
 * we want 7 significant digits, we return: eg. 123.4567
 */
export function formatSignificantDigits(value: number | string, digits: number) {
  const valueString = typeof value === "string" ? value : value.toString();
  const [integerPart, decimalPart] = valueString.split(".");
  const totalDigits = integerPart.length + (decimalPart?.length ?? 0);
  // if the total number of digits is less than the number of significant digits,
  // we return the value as is
  if (totalDigits <= digits) {
    return valueString;
  }
  // if the integer part has more digits than the number of significant digits,
  // we return the integer part
  if (integerPart.length >= digits) {
    return integerPart;
  }
  const valueNumber = typeof value === "string" ? parseFloat(value) : value;
  return valueNumber.toFixed(digits - integerPart.length);
}

export function formatAssetAmount(
  amount: string | number | undefined | null,
  asset: Asset,
  decimals?: number
): string {
  if (!amount) return "---";
  const numericAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(numericAmount)) return "---";

  const displayDecimals = decimals ?? asset?.display_decimals ?? 8;

  if (numericAmount < 10) {
    return formatSignificantDigits(numericAmount, 7);
  }
  if (numericAmount < 100) {
    return formatSignificantDigits(numericAmount, 6);
  }
  if (numericAmount >= 100000) {
    return formatNumber(numericAmount, Math.min(2, displayDecimals));
  }
  if (numericAmount >= 10000) {
    return formatNumber(numericAmount, Math.min(4, displayDecimals));
  }
  if (numericAmount >= 1000) {
    return formatNumber(numericAmount, Math.min(6, displayDecimals));
  }
  return formatNumber(numericAmount, displayDecimals);
}

export const formatDateTime = (
  date: string | DateTime,
  format: Intl.DateTimeFormatOptions = DateTime.DATETIME_SHORT,
  timezone: string = "America/Mexico_City"
) => {
  const dateTime = typeof date === "string" ? DateTime.fromISO(date) : date;
  return dateTime.setZone(timezone).toLocaleString(format);
};
